import { useState } from "react";
import { LogIn, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { ImpersonationUserRow } from "@/Api/impersonation";

interface ImpersonationTableProps {
  users: ImpersonationUserRow[];
  dataApi: ApiDataProps<ImpersonationUserRow>;
  pageActions: TypePageActions;
  isSuperAdmin: boolean;
  impersonatingUuid: string | null;
  onImpersonate: (user: ImpersonationUserRow) => void;
}

export function ImpersonationTable({
  users,
  dataApi,
  pageActions,
  isSuperAdmin,
  impersonatingUuid,
  onImpersonate,
}: ImpersonationTableProps) {
  const [selected, setSelected] = useState<ImpersonationUserRow | null>(null);

  const confirm = () => {
    if (!selected) return;

    const user = selected;
    setSelected(null);
    onImpersonate(user);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border bg-white dark:bg-[#2c2c2c]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Tipo</TableHead>
              {isSuperAdmin && <TableHead>Empresa</TableHead>}
              <TableHead>Cargos</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 6 : 5} className="text-center text-sm text-muted-foreground">
                  Nenhum usuário encontrado com os filtros informados.
                </TableCell>
              </TableRow>
            )}

            {users.map((user) => (
              <TableRow key={user.uuid}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{user.name}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                  </div>
                </TableCell>
                <TableCell>{user.user_type?.name || "-"}</TableCell>
                {isSuperAdmin && <TableCell>{user.tenant?.name || "-"}</TableCell>}
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.roles?.length
                      ? user.roles.map((role) => (
                          <Badge key={role.id} variant="outline">
                            {role.name}
                          </Badge>
                        ))
                      : "-"}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={cn(
                      user.is_active
                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200"
                        : "bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-200"
                    )}
                  >
                    {user.is_active ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!user.can_be_impersonated || impersonatingUuid === user.uuid}
                    title={
                      user.can_be_impersonated
                        ? "Acessar o sistema como este usuário"
                        : "Usuários administradores e inativos não podem ser acessados"
                    }
                    onClick={() => setSelected(user)}
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    Acessar como
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mb-3 flex justify-between">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page <= 1 && "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="size-8 cursor-auto border-2 border-blue-600 bg-transparent font-bold text-slate-900 hover:bg-transparent dark:text-white"
                >
                  <span className="pointer-events-none">{dataApi?.current_page}</span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="h-8 text-black hover:bg-stone-950/10 dark:text-white"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(
                    "h-8 bg-blue-600 hover:bg-blue-700",
                    dataApi?.current_page >= dataApi?.last_page && "pointer-events-none opacity-50"
                  )}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <AlertDialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Acessar como {selected?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Você passará a usar o sistema com as permissões de {selected?.email}. Tudo
              o que for feito ficará registrado no nome desse usuário, e o início e o fim
              do acesso ficam gravados no log com o seu usuário. A sessão expira em 3
              horas e você pode voltar à sua conta pela faixa no topo da tela.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>Acessar como usuário</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
